/**
 * SDX framework-agnostic observable primitive.
 *
 * Every hook core in this folder exposes its state via the same
 * subscribe + getSnapshot contract so each framework module can
 * plug its reactive primitive on top without knowing anything
 * about the underlying core:
 *
 * - React wraps the core in useSyncExternalStore, which
 *   explicitly consumes (subscribe, getSnapshot) pairs.
 * - Vue wraps the core in a shallowRef plus an onUnmounted
 *   subscribe + unsubscribe call.
 * - Svelte wraps the core in a readable store whose start
 *   function calls subscribe and returns the unsubscribe.
 *
 * The shape mirrors React's useSyncExternalStore signature on
 * purpose so the React adapter is a one-liner. The Vue and
 * Svelte adapters do one extra layer of indirection but pay no
 * conceptual cost - the contract is small enough to implement
 * in-line without a helper.
 *
 * Implementations should be the single source of truth for their
 * domain (form state, async loading, presence set, ...) and must
 * never reach into framework globals like drupalSettings or
 * window directly unless the state they expose is derived from
 * them. Doing so would leak framework-specific assumptions into
 * a file that is deliberately framework-agnostic.
 */

export type Listener<T> = (snapshot: Readonly<T>) => void;

export type Unsubscribe = () => void;

export interface Observable<T> {
  /**
   * Returns the current snapshot. Must be synchronous and
   * reference-stable between mutations so React's
   * useSyncExternalStore can short-circuit on identity.
   */
  getSnapshot(): Readonly<T>;

  /**
   * Registers a listener that is invoked with every snapshot
   * transition. Returns an unsubscribe function that removes
   * the listener and must be safe to call multiple times.
   */
  subscribe(listener: Listener<T>): Unsubscribe;

  /**
   * Returns the snapshot to render with on the server. React
   * throws from useSyncExternalStore without it and falls the
   * whole subtree back to client-only rendering.
   */
  getServerSnapshot?(): Readonly<T>;
}

/**
 * Minimal Observable implementation with in-place state
 * replacement and synchronous notification.
 *
 * Subclasses use `apply(patch)` to commit state changes: the
 * patch is merged on top of the current snapshot, a new frozen
 * snapshot is produced, and every subscriber is invoked before
 * the call returns. Errors thrown by a subscriber are caught
 * and logged so one misbehaving consumer cannot break every
 * other subscriber.
 */
export class ObservableStore<T extends object> implements Observable<T> {
  protected snapshot: Readonly<T>;
  private readonly listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.snapshot = Object.freeze({ ...initial });
  }

  getSnapshot = (): Readonly<T> => this.snapshot;

  /**
   * Hook cores are constructed fresh on every SSR pass and again
   * on the client mount, so the current snapshot IS the initial
   * state whenever React consults the server snapshot - both
   * during SSR and during the hydration render. Subclasses whose
   * server state must differ from their initial client state
   * (e.g. ConfirmModalStore) override this with a constant.
   */
  getServerSnapshot = (): Readonly<T> => this.snapshot;

  subscribe = (listener: Listener<T>): Unsubscribe => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Commits a partial state update and notifies every listener.
   *
   * Returns the new snapshot so subclasses that want to chain
   * work off the fresh state can read it without a second
   * getSnapshot() call.
   */
  protected apply(patch: Partial<T>): Readonly<T> {
    this.snapshot = Object.freeze({ ...this.snapshot, ...patch });
    this.notify();
    return this.snapshot;
  }

  /**
   * Replaces the entire snapshot with `next` and notifies every
   * listener. Subclasses whose state shape is not amenable to
   * a shallow merge use this instead of apply().
   */
  protected replace(next: T): Readonly<T> {
    this.snapshot = Object.freeze({ ...next });
    this.notify();
    return this.snapshot;
  }

  private notify(): void {
    for (const listener of Array.from(this.listeners)) {
      try {
        listener(this.snapshot);
      }
      catch (err) {
        console.warn('[SDX Observable] Listener threw, continuing notification.', err);
      }
    }
  }
}
